import React, { useEffect, useState } from 'react';
import { useAccount } from '@/account/useAccount';
import { PremiumDataModal } from '@/components/PremiumDataModal/PremiumDataModal';
import { usePremiumDataModal } from '@/components/PremiumDataModal/hooks/usePremiumDataModal';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { PremiumPaymentSuccessModal } from '@/reports/shared/PremiumPaymentModal/PremiumPaymentSuccessModal';
import { Collapse } from '@/theme/index';
import { checkSingleReportVariations } from '@/utils/checkSingleReportVariations';
import { MobileSearchTab, StyledMobileSearchTabs } from './components';
import { SearchFormsContainer } from '../../SearchFormsContainer/SearchFormsContainer';
import {
  SearchFormProps,
  SearchTabData,
} from '../../SearchFormsContainer/interfaces';

interface MobileHeaderSearchProps {
  onSearchSuccess: SearchFormProps['onSuccess'];
  onSearchError: SearchFormProps['onError'];
  closeForm: () => void;
  setSelectedTab: React.Dispatch<React.SetStateAction<SearchTabData | null>>;
  searchFormsIsMounted: boolean;
  showMobilePrimarySearchFormTabs?: boolean;
  selectedTab: SearchTabData | null;
  combinedPropertySearch?: boolean;
  isSearchFormLocked?: boolean;
  shouldHideSearchForm?: boolean;
}

export function MobileHeaderSearch({
  onSearchSuccess,
  onSearchError,
  closeForm,
  setSelectedTab,
  searchFormsIsMounted,
  showMobilePrimarySearchFormTabs,
  selectedTab,
  combinedPropertySearch,
  isSearchFormLocked,
  shouldHideSearchForm,
}: MobileHeaderSearchProps) {
  const { trackEvent, routingUtils } = useAppConfigContext();
  const {
    config: { searchTabs },
  } = useAppConstantsContext();

  const { data: account } = useAccount(true);
  const { variationPlanNames, internationalPlanNames } =
    useAppConstantsContext();

  const reportVariations =
    account && internationalPlanNames
      ? checkSingleReportVariations(
          account,
          variationPlanNames,
          internationalPlanNames
        )
      : null;
  const isInSingleReportVariation =
    reportVariations?.isInSingleReportVariation ?? false;
  const isInInternationalLanguageTest =
    reportVariations?.isInInternationalLanguageTest ?? false;
  const isInternationalVinTest =
    reportVariations?.isInternationalVinTest ?? false;

  const { isModalOpen, closeModal, triggerModal } = usePremiumDataModal();

  const { navigate } = routingUtils;
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [isSuccessModaVisible, setIsSuccessModaVisible] =
    useState<boolean>(false);

  const openSuccessModal = () => {
    setIsSuccessModaVisible(true);
  };

  const closeModals = () => {
    setIsSuccessModaVisible(false);
    setPaymentShowModalNumber(0);
  };

  const onSuccessPaymentHandler = () => {
    closeModals();
    navigate('/dashboard');
  };

  useEffect(() => {
    setTimeout(() => {
      setSelectedTab(searchTabs[0]);
    }, 500);
  }, [open]);

  let filteredSearchTabs = searchTabs;

  if (isInInternationalLanguageTest) {
    filteredSearchTabs = [];
  } else if (isInternationalVinTest) {
    filteredSearchTabs = searchTabs.filter((tab) => !tab.isHidden);
  }

  // Brands that only have one search tab should not display the external selectedTab
  const currentlyDisplayedTab =
    filteredSearchTabs.length === 1 ? filteredSearchTabs[0] : selectedTab;

  const clickHandler = (tab: SearchTabData) => {
    setSelectedTab(tab);
    trackEvent(
      `${tab.displayName} search`,
      'navigation click',
      'header search'
    );
    triggerModal(tab.isLocked, isInSingleReportVariation);
  };

  return (
    <>
      <Collapse in={searchFormsIsMounted} timeout={500} unmountOnExit>
        {!shouldHideSearchForm && showMobilePrimarySearchFormTabs && (
          <StyledMobileSearchTabs>
            {filteredSearchTabs.map((tab) => (
              <MobileSearchTab
                key={tab.id}
                onClick={() => clickHandler(tab)}
                className={`${
                  currentlyDisplayedTab?.id === tab.id ? 'active' : ''
                } ${tab.class}`}
              >
                <span className="tab-icon">{tab.icon}</span>
                <p className="tab-name">{tab.displayName}</p>
              </MobileSearchTab>
            ))}
          </StyledMobileSearchTabs>
        )}
        <SearchFormsContainer
          {...currentlyDisplayedTab}
          displayName={currentlyDisplayedTab?.displayName || ''}
          closeForm={closeForm}
          onSearchSuccess={onSearchSuccess}
          onSearchError={onSearchError}
          placement="header"
          combinedPropertySearch={combinedPropertySearch}
          isSearchFormLocked={isSearchFormLocked}
          shouldHideSearchForm={shouldHideSearchForm}
        />
      </Collapse>
      {isInSingleReportVariation && (
        <PremiumDataModal
          isOpen={isModalOpen}
          onClose={closeModal}
          searchType={selectedTab?.displayName}
          paymentShowModalNumber={paymentShowModalNumber}
          setPaymentShowModalNumber={setPaymentShowModalNumber}
          openSuccessModal={openSuccessModal}
        />
      )}
      <PremiumPaymentSuccessModal
        open={isSuccessModaVisible}
        onClose={closeModals}
        onAccept={onSuccessPaymentHandler}
      />
    </>
  );
}
